import { upliftHttpClient } from '@/services/http/clients/upliftHttpClient';
import { useLoadingAnimation } from '@/components/LoadingAnimation/useLoadingAnimation.hook';
import { DEFAULT_ERROR_MESSAGE } from '@/config/text.const';
import { useSaveFileLocally } from '@/hooks/useSaveFileLocally.hook';
import { notificationService } from '@/services/notification/notification.client';
import { DateType } from '@/components/common/DatePicker/DatePicker';

export type DateRangeAndInterval = {
  dateFrom?: DateType;
  dateTo?: DateType;
  billingIntervals?: string[] | null;
};

export default function useDownloadUpliftTemplate() {
  const { toggleLoadingAnimation } = useLoadingAnimation();
  const { saveFileLocally, getFileNameFromContentDisposition } = useSaveFileLocally();

  const downloadFile =
    async (dateRangeAndInterval: DateRangeAndInterval, callback?: Function) => {
      try {
        toggleLoadingAnimation(true);
        if (Object.keys(dateRangeAndInterval).length === 0) {
          throw new Error('There is no valid parameter in the date range');
        }
        const requestPath = '/Documents/upliftsTemplateByBillingCycles';
        const response = await upliftHttpClient.post(requestPath, {
          ...dateRangeAndInterval
        }, {
          responseType: 'arraybuffer',
        });
        if (response?.data) {
          const blob = new Blob([response.data]);
          const fileName = getFileNameFromContentDisposition(response.headers['content-disposition']);
          saveFileLocally(blob, fileName);
          if (typeof callback === 'function') {
            callback();
          }
        } else {
          throw new Error('Something went wrong. The file cannot be downloaded.');
        }
      }
      catch (err: any) {
        let errorMessage = DEFAULT_ERROR_MESSAGE;

        if (err?.response?.data instanceof ArrayBuffer && err.response.data.byteLength > 0) {
          // responseType is 'ArrayBuffer' since we are expecting a file from the server
          // but in an error case, the error message will also be an ArrayBuffer
          // so we need to convert it to text
          const textDcoder = new TextDecoder();
          const errorText = JSON.parse(textDcoder.decode(err.response.data))?.message;

          if (errorText) {
            errorMessage = errorText;
          }
        }
        else if (err?.message) {
          errorMessage = err.message;
        }

        notificationService.open({
          title: 'Error',
          text: errorMessage,
          type: 'danger'
        });
      }

      toggleLoadingAnimation(false);
    };

  return {
    downloadFile
  } as const;
}
